import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ── Types ──────────────────────────────────────────────────────────

export interface SegmentData {
  buyer_segment?: string;
  seniority?: string;
  persona_summary?: string;
  primary_pain_points?: string[];
  messaging_angle?: string;
  value_hook?: string;
  decision_role?: string;
  buying_context?: string;
  sso_relevance?: string;
  reasoning?: string;
  confidence?: string;
}

export interface ContactDetailFull {
  // Core identity
  id: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  // Vendor raw values. Parser INPUTS only — never display or filter on these directly.
  department: string | null;
  seniority_level: string | null;
  // Canonical classification (5 seniority tiers / 18 departments). NULL means unknown,
  // which is meaningful: the legacy columns used 'Unknown'/'OTHER' filler and were
  // therefore always "populated" even when they knew nothing.
  carr_seniority: string | null;
  carr_department: string | null;
  email: string | null;
  email_status: string | null;
  email_verified: boolean | null;
  linkedin_url: string | null;
  phone: string | null;
  location: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  source: string | null;
  biography: string | null;
  // Classification (L2)
  buyer_segment: string | null;
  segment_confidence: string | null;
  segment_source: string | null;
  segment_classified_at: string | null;
  segment_data: SegmentData | null;
  decision_role: string | null;
  is_decision_maker: boolean | null;
  // CARR-specific classification fields
  pain_point_category: string | null;
  messaging_variant_default: string | null;
  lead_score: number | null;
  lead_temperature: string | null;
  engagement_score: number | null;
  decision_authority: string | null;
  is_champion: boolean | null;
  // Clay enrichment
  clay_enrichment_status: string | null;
  clay_enriched_at: string | null;
  clay_batch_id: string | null;
  clay_headline: string | null;
  clay_summary: string | null;
  clay_location: string | null;
  clay_country: string | null;
  clay_connections: number | null;
  clay_num_followers: number | null;
  clay_picture_url: string | null;
  clay_experience_title: string | null;
  clay_experience_company: string | null;
  clay_experience_summary: string | null;
  clay_experience_is_current: boolean | null;
  clay_experience_url: string | null;
  clay_experience_company_domain: string | null;
  clay_publication_title: string | null;
  clay_publication_summary: string | null;
  clay_publication_publisher: string | null;
  clay_publication_url: string | null;
  clay_publication_date: string | null;
  // Additional enrichment
  therapeutic_focus: string[] | null;
  technology_focus: string[] | null;
  enriched_role_description: string | null;
  // CRUD fields (used by Actions tab)
  marketing_persona: string | null;
  outreach_status: string | null;
  last_contacted_at: string | null;
  next_followup_date: string | null;
  next_followup_action: string | null;
  last_engagement_type: string | null;
  last_engagement_date: string | null;
  contact_notes: string | null;
  notes: string | null;
  tags: string[] | null;
  created_at: string;
  updated_at: string;
  // Company (joined)
  company_id: string | null;
  company_name: string | null;
  company_domain: string | null;
  company_description: string | null;
  company_primary_category: string | null;
  company_icp_score: number | null;
  company_icp_tier: string | null;
  company_employee_count: number | null;
  company_website: string | null;
  company_hq_state: string | null;
  company_hq_country: string | null;
  company_icp_segment: string | null;
}

export interface ContactOutreachMessage {
  id: string;
  message_type: string | null;
  status: string | null;
  generated_text: string | null;
  final_text: string | null;
  messaging_angle: string | null;
  sender_name: string | null;
  created_at: string;
  sent_at: string | null;
  reply_received_at: string | null;
  reply_text: string | null;
  reply_sentiment: string | null;
  campaign_id: string | null;
}

// ── Queries ────────────────────────────────────────────────────────

const CONTACT_COLUMNS = `
  id, full_name, first_name, last_name, job_title, department,
  seniority_level, carr_seniority, carr_department,
  email, email_status, email_verified, linkedin_url, phone,
  location, city, state, country, source, biography,
  buyer_segment, segment_confidence, segment_source,
  segment_classified_at, segment_data, decision_role, is_decision_maker,
  pain_point_category, messaging_variant_default,
  lead_score, lead_temperature, engagement_score, decision_authority, is_champion,
  clay_enrichment_status, clay_enriched_at, clay_batch_id,
  clay_headline, clay_summary, clay_location, clay_country,
  clay_connections, clay_num_followers, clay_picture_url,
  clay_experience_title, clay_experience_company, clay_experience_summary,
  clay_experience_is_current, clay_experience_url, clay_experience_company_domain,
  clay_publication_title, clay_publication_summary, clay_publication_publisher,
  clay_publication_url, clay_publication_date,
  therapeutic_focus, technology_focus, enriched_role_description,
  marketing_persona, buyer_segment, outreach_status,
  last_contacted_at, next_followup_date, next_followup_action,
  last_engagement_type, last_engagement_date, contact_notes, notes, tags,
  created_at, updated_at,
  company_id, company_name,
  companies!contacts_company_id_fkey (
    company_name, domain, description, primary_category,
    icp_score, icp_tier, employee_count, website,
    headquarters_state, headquarters_country, icp_segment
  )
`;

export function useContactDetailFull(contactId: string | null) {
  return useQuery({
    queryKey: ["contact-detail-full", contactId],
    queryFn: async (): Promise<ContactDetailFull | null> => {
      if (!contactId) return null;

      const { data, error } = await (supabase as any)
        .from("contacts")
        .select(CONTACT_COLUMNS)
        .eq("id", contactId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const company = data.companies as any;
      return {
        ...data,
        company_name: company?.company_name ?? data.company_name ?? null,
        company_domain: company?.domain ?? null,
        company_description: company?.description ?? null,
        company_primary_category: company?.primary_category ?? null,
        company_icp_score: company?.icp_score ?? null,
        company_icp_tier: company?.icp_tier ?? null,
        company_employee_count: company?.employee_count ?? null,
        company_website: company?.website ?? null,
        company_hq_state: company?.headquarters_state ?? null,
        company_hq_country: company?.headquarters_country ?? null,
        company_icp_segment: company?.icp_segment ?? null,
        companies: undefined,
      } as unknown as ContactDetailFull;
    },
    enabled: !!contactId,
    staleTime: 30_000,
  });
}

export function useContactOutreachMessages(contactId: string | null) {
  return useQuery({
    queryKey: ["contact-outreach-messages", contactId],
    queryFn: async (): Promise<ContactOutreachMessage[]> => {
      if (!contactId) return [];

      const { data, error } = await (supabase as any)
        .from("outreach_messages")
        .select(`
          id, message_type, status, generated_text, final_text,
          messaging_angle, sender_name, created_at, sent_at,
          reply_received_at, reply_text, reply_sentiment, campaign_id
        `)
        .eq("contact_id", contactId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as ContactOutreachMessage[];
    },
    enabled: !!contactId,
    staleTime: 30_000,
  });
}
